import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import axios from 'axios'
import errorLogger from '../utils/vueErrorLogger.js'
import LandingPage from '../components/LandingPage.vue'
import WorkspaceComponent from '../components/WorkspaceComponent.vue'
import BookingPage from '../components/BookingPage.vue'
import NotFoundPage from '../components/NotFoundPage.vue'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: LandingPage
  },
  {
    path: '/book/:businessSlug/:appointmentId?',
    name: 'Booking',
    component: BookingPage,
    props: true,
    beforeEnter: async (to, from, next) => {
      const { businessSlug } = to.params;

      try {
        let businessExists = false;
        try {
          const response = await axios.get(`/api/businesses/check/${businessSlug}`);
          const data = response.data;

          if (data.code == 'ok') {
            businessExists = true;
          }

        } catch (error) {
          console.error('API error:', error);
        }

        if (businessExists) {
          next();
        } else {
          next({ name: 'not-found' });
        }
      } catch (error) {
        console.error('Error checking business:', error);
        next({ name: 'not-found' });
      }
    }
  },
  {
    path: '/workspace',
    component: WorkspaceComponent,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        redirect: '/workspace/dashboard'
      },
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('../components/DashboardSection.vue'),
        meta: { requiresAuth: true, requiresPermission: true }
      },
      {
        path: 'calendar',
        name: 'Calendar',
        component: () => import('../components/CalendarSection.vue'),
        meta: { requiresAuth: true, requiresPermission: true }
      },
      {
        path: 'appointments',
        name: 'Appointments',
        component: () => import('../components/AppointmentListSection.vue'),
        meta: { requiresAuth: true, requiresPermission: true }
      },
      {
        path: 'services',
        name: 'Services',
        component: () => import('../components/ServicesSection.vue'),
        meta: { requiresAuth: true, requiresPermission: true }
      },
      {
        path: 'providers',
        name: 'Providers',
        component: () => import('../components/ProvidersSection.vue'),
        meta: { requiresAuth: true, requiresPermission: true }
      },
      {
        path: 'clients',
        name: 'Clients',
        component: () => import('../components/ClientsSection.vue'),
        meta: { requiresAuth: true, requiresPermission: true }
      },
      {
        path: 'clients/:id',
        name: 'ClientDetail',
        component: () => import('../components/ClientDetail.vue'),
        props: route => ({ clientId: route.params.id }),
        meta: { requiresAuth: true, requiresPermission: true }
      },
      {
        path: 'subscriptions',
        name: 'Subscriptions',
        component: () => import('../components/SubscriptionsSection.vue'),
        meta: { requiresAuth: true, requiresPermission: true },
        beforeEnter: async (to, from, next) => {
          try {
            const response = await axios.get('/api/settings/features/');
            if (response?.data?.code === 'ok') {
              const features = response.data.data?.features || [];
              const subscriptionsFeature = features.find(f => f.code === 'subscriptions');

              if (subscriptionsFeature && subscriptionsFeature.isEnabled) {
                next();
              } else {
                next({ name: 'Access Denied' });
              }
            } else {
              next({ name: 'Access Denied' });
            }
          } catch (error) {
            console.error('Error checking subscriptions feature:', error);
            next({ name: 'Access Denied' });
          }
        }
      },
      {
        path: 'settings/rules',
        name: 'Booking Rules',
        component: () => import('../components/BookingRules.vue'),
        meta: { requiresAuth: true, requiresPermission: true }
      },
      {
        path: 'settings/info',
        name: 'Business Information',
        component: () => import('../components/BusinessInfo.vue'),
        meta: { requiresAuth: true, requiresPermission: true }
      },
      {
        path: 'settings/working-hours',
        name: 'Business Working Hours',
        component: () => import('../components/BusinessAllWorkingHours.vue'),
        meta: { requiresAuth: true, requiresPermission: true }
      },
      {
        path: 'settings/features',
        name: 'Features',
        component: () => import('../components/AdditionalFeatures.vue'),
        meta: { requiresAuth: true, requiresPermission: true }
      },
      {
        path: 'settings/team',
        name: 'Team',
        component: () => import('../components/TeamManagement.vue'),
        meta: { requiresAuth: true, requiresPermission: true }
      },
      {
        path: 'settings/tags',
        name: 'Tags',
        component: () => import('../components/TagsSection.vue'),
        meta: { requiresAuth: true, requiresPermission: true }
      },
      {
        path: 'settings/booking-form',
        name: 'Booking Forms',
        component: () => import('../components/BookingFormSection.vue'),
        meta: { requiresAuth: true, requiresPermission: true }
      },
      {
        path: 'account-settings',
        name: 'Account Settings',
        component: () => import('../components/AccountSettings.vue'),
        meta: { requiresAuth: true, requiresPermission: true }
      },
      {
        path: 'report',
        name: 'Report',
        component: () => import('../components/IssueReport.vue'),
        meta: { requiresAuth: true }
      }
    ]
  },
  {
    path: '/onboarding',
    name: 'Onboarding',
    component: () => import('../components/OnboardingComponent.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/onboarding/complete',
    name: 'Onboarding Complete',
    component: () => import('../components/OnboardingComplete.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../components/LoginForm.vue')
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('../components/RegistrationForm.vue')
  },
  {
    path: '/contact-us',
    name: 'Contact Us',
    component: () => import('../components/ContactUs.vue')
  },
  {
    path: '/public/appointments/cancel/:businessSlug/:appointmentId',
    name: 'Verify Cancellation',
    beforeEnter: async (to, from, next) => {
      const { businessSlug, appointmentId } = to.params;
      const { token } = to.query;

      try {
        const response = await axios.get(`/api/public/appointments/verify-cancellation`, {
          params: {
            slug: businessSlug,
            appointmentId: appointmentId,
            token: token
          }
        });
        const data = response.data;

        if (data.code == 'ok' && data.data?.isTokenValid) {
          next({
            name: 'Cancel Appointment',
            params: { businessSlug, appointmentId },
            query: {
              ...to.query, // Keep existing query params (like token)
              appointment: JSON.stringify(data.data?.appointment)
            }
          });
        } else if (data.data?.isBusinessNotFound) {
          next({ name: 'Business Inactive' });
        } else {
          // Check if there are custom messages from the database
          const hasCustomMessages = data.messages && data.messages.length > 0;

          if (hasCustomMessages) {
            // Use the first message from the database
            next({
              name: 'Appointment Action Error',
              query: {
                actionType: 'cancel',
                customMessage: data.messages[0]
              }
            });
          } else {
            next({
              name: 'Appointment Action Error',
              query: {
                actionType: 'cancel',
                errorType: data.data?.isTokenInvalid ? 'invalid_token' :
                  data.data?.isAppointmentNotFound ? 'appointment_not_found' :
                    'unknown_error'
              }
            });
          }
        }
      } catch (error) {
        console.error('API error:', error);
        if (error?.response?.data?.data?.isAlreadyCancelled) {
          next({
            name: 'Appointment Action Error',
            query: {
              actionType: 'cancel',
              errorType: 'already_cancelled'
            }
          });
        } else if (error?.response?.data?.data?.isCancelingPastAppointment) {
          next({
            name: 'Appointment Action Error',
            query: {
              actionType: 'cancel',
              errorType: 'past_appointment'
            }
          });
        } else if (error?.response?.data?.messages?.length > 0) {
          next({
            name: 'Appointment Action Error',
            query: {
              actionType: 'cancel',
              customMessage: error?.response?.data?.messages[0]
            }
          });
        } else {
          next({
            name: 'Appointment Action Error',
            query: {
              actionType: 'cancel',
              errorType: 'unknown_error'
            }
          });
        }
      }
    }
  },
  {
    path: '/public/appointments/cancel/:businessSlug/:appointmentId',
    name: 'Cancel Appointment',
    component: () => import('../components/CancellationComponent.vue'),
    props: route => ({
      businessSlug: route.params.businessSlug,
      appointmentId: route.params.appointmentId,
      token: route.query.token,
      appointmentData: route.query.appointment ? JSON.parse(route.query.appointment) : null
    })
  },
  {
    path: '/public/appointments/reschedule/:businessSlug/:appointmentId',
    name: 'Verify Reschedule',
    props: true,
    beforeEnter: async (to, from, next) => {
      const { businessSlug, appointmentId } = to.params;
      const { token } = to.query;

      try {
        const response = await axios.get(`/api/public/appointments/verify-reschedule`, {
          params: {
            slug: businessSlug,
            appointmentId: appointmentId,
            token: token
          }
        });
        const data = response.data;
        
        if (data.code == 'ok' && data.data?.isTokenValid) {
          next({
            name: 'Booking',
            params: { businessSlug, appointmentId },
            query: {
              ...to.query, // Keep existing query params (like token)
              appointment: JSON.stringify(data.data?.appointment)
            }
          });
        } else if (data.data?.isBusinessNotFound) {
          next({ name: 'Business Inactive' });
        } else {
          // Check if there are custom messages from the database
          const hasCustomMessages = data.messages && data.messages.length > 0;

          if (hasCustomMessages) {
            // Use the first message from the database
            next({
              name: 'Appointment Action Error',
              query: {
                actionType: 'reschedule',
                customMessage: data.messages[0]
              }
            });
          } else {
            next({
              name: 'Appointment Action Error',
              query: {
                actionType: 'reschedule',
                errorType: data.data?.isTokenInvalid ? 'invalid_token' :
                  data.data?.isAppointmentNotFound ? 'appointment_not_found' :
                    'unknown_error'
              }
            });
          }
        }
      } catch (error) {
        //console.error('API error:', error);

        if (error?.response?.data?.isBusinessNotFound) {
          next({ name: 'Business Inactive' });
        } else if (error?.response?.data?.data?.isReschedulingPastAppointment) {
          next({
            name: 'Appointment Action Error',
            query: {
              actionType: 'reschedule',
              errorType: 'past_appointment',
              businessSlug: businessSlug
            }
          });
        } else {
          // Check if there are custom messages from the database
          const hasCustomMessages = error?.response?.data?.messages && error?.response?.data?.messages.length > 0;

          if (hasCustomMessages) {
            next({
              name: 'Appointment Action Error',
              query: {
                actionType: 'reschedule',
                customMessage: error.response.data.messages[0]
              }
            });
          } else {
            next({
              name: 'Appointment Action Error',
              query: {
                actionType: 'reschedule',
                errorType: error?.response?.data?.data?.isTokenInvalid ? 'invalid_token' :
                  error?.response?.data?.data?.isAppointmentNotFound ? 'appointment_not_found' :
                    'unknown_error'
              }
            });
          }
        }

        return;
      }
    }
  },
  {
    path: '/verify-email',
    name: 'Email Verification',
    component: () => import('../components/EmailVerification.vue')
  },
  {
    path: '/forgot-password',
    name: 'Password Recovery',
    component: () => import('../components/ForgotPasswordForm.vue')
  },
  {
    path: '/404',
    name: 'not-found',
    component: NotFoundPage
  },
  {
    path: '/:catchAll(.*)',
    redirect: { name: 'not-found' }
  },
  {
    path: '/invitation-error',
    name: 'Invitation Error',
    component: () => import('../components/InvitationError.vue')
  },
  {
    path: '/invitations/accept',
    name: 'AcceptInvitation',
    beforeEnter: async (to, from, next) => {
      const token = to.query.token;

      if (!token) {
        // No token provided - redirect to regular registration
        next('/register');
        return;
      }

      try {
        // Validate the invitation token
        const response = await axios.get(`/api/invitations/validate/${token}`);
        const data = response.data;
        
        if (response.status === 200 && data.code == 'ok') {
          // Token is valid - redirect to registration with invitation data
          next({
            path: '/register',
            query: {
              token: token,
              business: data.data?.business,
              inviter: data.data?.inviter
            }
          });
        } else {
          // Token is invalid/expired - redirect to error page or show message
          next({
            path: '/invitation-error',
            query: {
              error: 'invitation_invalid',
              message: data.message || 'Šis uzaicinājums nav derīgs vai arī tam beidzies termiņš'
            }
          });
        }
      } catch (error) {
        next({
          path: '/invitation-error',
          query: {
            error: 'invitation_error',
            message: 'Neizdevās verificēt uzaicinājumu. Lūdzu pamēģiniet vēlreiz.'
          }
        });
      }
    }
  },
  {
    path: '/reset-password',
    name: 'Reset Password',
    component: () => import('../components/ResetPasswordForm.vue')
  },
  {
    path: '/public/appointments/attendance/:mode/:businessSlug/:appointmentId',
    name: 'Verify Appointment Confirm',
    props: true,
    beforeEnter: async (to, from, next) => {
      const { businessSlug, appointmentId, mode } = to.params;
      const { token } = to.query;

      try {
        const response = await axios.get(`/api/public/appointments/verify-confirmation`, {
          params: {
            slug: businessSlug,
            appointmentId: appointmentId,
            token: token
          }
        });
        const data = response.data;
        
        if (data.code == 'ok' && data.data?.isTokenValid) {
          next({
            name: 'Confirm Appointment',
            query: {
              ...to.query,
              businessSlug: businessSlug,
              appointmentId: appointmentId,
              mode: mode,
              appointment: JSON.stringify(data.data?.appointment)
            }
          });
        } else if (data.data?.isBusinessNotFound) {
          next({ name: 'Business Inactive' });
        } else {
          next({
            name: 'Appointment Action Error',
            query: {
              actionType: 'confirm',
              errorType: data.data?.isTokenInvalid ? 'invalid_token' : 
                       data.data?.isAppointmentNotFound ? 'appointment_not_found' : 
                       'unknown_error'
            }
          });
        }
      } catch (error) {
        console.error('Error checking business:', error);
        next({
          name: 'Appointment Action Error',
          query: {
            actionType: 'confirm',
            errorType: 'unknown_error'
          }
        });
      }
    }
  },
  {
    path: '/public/appointments/confirm/',
    name: 'Confirm Appointment',
    component: () => import('../components/AppointmentConfirmation.vue'),
    props: route => ({
      businessSlug: route.query.businessSlug,
      appointmentId: route.query.appointmentId,
      token: route.query.token,
      mode: route.query.mode,
      appointmentData: route.query.appointment ? JSON.parse(route.query.appointment) : null
    })
  },
  {
    path: '/appointments/error',
    name: 'Appointment Action Error',
    props: route => ({
      actionType: route.query.actionType,
      errorType: route.query.errorType
    }),
    component: () => import('../components/AppointmentActionError.vue')
  },
  {
    path: '/business-inactive',
    name: 'Business Inactive',
    component: () => import('../components/InactiveBusinessPage.vue')
  },
  {
    path: '/access-denied',
    name: 'Access Denied',
    component: () => import('../components/AccessDenied.vue')
  },
  {
    path: '/rate-limit-exceeded',
    name: 'RateLimitError',
    component: () => import('../components/RateLimitError.vue')
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    // Always scroll to top when navigating to a new route
    return { top: 0 }
  }
})

// Handle chunk loading errors by reloading the page
router.onError((error, to, from) => {
  if (/Loading chunk \d+ failed/.test(error.message) ||
      /Failed to fetch dynamically imported module/.test(error.message) ||
      error.message.includes('ChunkLoadError')) {

    // Use the target route path if available, otherwise fall back to current URL
    const targetPath = to?.fullPath || window.location.pathname + window.location.search
    const currentPath = from?.fullPath || router.currentRoute.value.fullPath

    // Log the chunk loading error to backend
    errorLogger.logError({
      type: 'router_chunk_load_error',
      message: error.message,
      stack: error.stack,
      targetPath: targetPath,
      currentPath: currentPath,
      url: window.location.href,
      userAgent: navigator.userAgent
    })

    // Check if we've recently reloaded to prevent infinite reload loops
    const lastReload = sessionStorage.getItem('last-chunk-reload')
    const now = Date.now()

    // Use 30 seconds instead of 10 to be safer against slow connections
    if (!lastReload || (now - parseInt(lastReload)) > 30000) {
      // Store reload timestamp and target path
      sessionStorage.setItem('last-chunk-reload', now.toString())
      sessionStorage.setItem('reload-target-path', targetPath)

      // Force a full page reload to get the new version
      window.location.href = targetPath
    } else {
      console.error('Chunk load failed multiple times, preventing reload loop:', error)

      // Log when we prevent reload loop
      errorLogger.logError({
        type: 'router_chunk_load_error_reload_prevented',
        message: 'Chunk load failed multiple times, preventing reload loop',
        originalError: error.message,
        targetPath: targetPath,
        url: window.location.href,
        userAgent: navigator.userAgent
      })
    }
  }
})

router.beforeEach(async (to, from, next) => {
  const auth = useAuthStore()

  // Always initialize auth to ensure we have current status
  await auth.initializeAuth()

  // Check if route requires auth
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    next('/')
    return
  }

  // Check if route requires specific permissions
  // Only check permissions for authenticated users AND when permissions are ready
  if (to.meta.requiresPermission && auth.isAuthenticated) {
    // Wait for permissions to be initialized before checking access
    if (!auth.isPermissionsReady) {
      // Permissions not ready yet, allow navigation for now
      // The component itself should handle loading states
      console.warn('Permissions not initialized yet, allowing navigation')
      next()
      return
    }

    if (!auth.canAccessRoute(to.name)) {
      next({ name: 'Access Denied' })
      return
    }
  }

  next()
})

export default router